import React, { useEffect, useState } from "react";
import UsagesGraphComponent from "../../../layout/UsagesGraphComponent";
import { KTIcon } from "helpers";
import UsageProgressBar from "./components/UsageProgressBar";
import { getFeatureDetails } from "helpers/utils";
import { RiRobot2Fill } from "react-icons/ri";
import { FaLayerGroup } from "react-icons/fa";
import { MdScheduleSend } from "react-icons/md";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { privateRoutes } from "constant/route/privateRoutes";
import { useAuth } from "hooks‬/Auth";
import PageLoader from "components/base/PageLoader";

import Select from "react-select";
import SapiensUsage from "./components/SapiensUsage";
import "./SubscriptionUsage.scss";

const SubscriptionUsage = () => {
  const {
    companySubscriptionDetails,
    companyProductSubscriptionDetails,
    subscriptionFetchError,
    fetchSubcriptionDetail,
  } = useAuth();
  // companySubscriptionDetails is a single, non-product-specific record - for
  // a company with multiple products it doesn't necessarily correspond to
  // Hire, which is why Hire and Sapiens could end up showing the same
  // numbers. Scope this view to the Hire entry specifically, the same way
  // SapiensUsage already scopes to Sapiens.
  const hireSubscriptionDetails = companyProductSubscriptionDetails?.find(
    (product: any) =>
      product?.subscriptionPricingPlan?.subscriptionPlanProduct?.productType
        ?.name === "HIRE",
  );
  const [interviewsconductedGraphData, setInterviewsconductedGraphData] =
    React.useState<any>();
  const [jobsPostedGraphData, setJobsPostedGraphData] = React.useState<any>();
  const [resumesUploadedGraphDat, setResumesUploadedGraphDat] = useState<any>();

  // Only offer products the company actually subscribes to - picking the
  // one they don't have used to render with no plan/features/usage at all.
  const PRODUCT_LABELS: Record<string, string> = { HIRE: "Hire", SAPIENS: "Sapiens" };
  const productOptions = (companyProductSubscriptionDetails ?? [])
    .map(
      (product: any) =>
        product?.subscriptionPricingPlan?.subscriptionPlanProduct?.productType
          ?.name,
    )
    .filter(Boolean)
    .map((name: string) => ({ label: PRODUCT_LABELS[name] ?? name, value: name }));

  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [isDataReady, setIsDataReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const stillValid = productOptions.some(
      (option: any) => option.value === selectedOption?.value,
    );
    if (!stillValid && productOptions.length > 0) {
      setSelectedOption(productOptions[0]);
    }
    // eslint-disable-next-line
  }, [companyProductSubscriptionDetails]);

  useEffect(() => {
    if (hireSubscriptionDetails) {
      setTimeout(() => {
        setJobsPostedGraphData(
          hireSubscriptionDetails.companySubscriptionUsage?.find(
            (feature: any) => feature.features.featuresName === "CREATE_JOBS",
          ),
        );
        setResumesUploadedGraphDat(
          hireSubscriptionDetails.companySubscriptionUsage?.find(
            (feature: any) =>
              feature.features.featuresName === "CREATE_CANDIDATES",
          ),
        );
        setInterviewsconductedGraphData(
          hireSubscriptionDetails.companySubscriptionUsage?.find(
            (feature: any) =>
              feature.features.featuresName === "SCHEDULE_INTERVIEWS",
          ),
        );
        setIsDataReady(true);
      }, 300);
    }
  }, [hireSubscriptionDetails]);

  if (companySubscriptionDetails === undefined && subscriptionFetchError) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-20">
        <p className="text-muted mb-4">
          We couldn't load your subscription details.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => fetchSubcriptionDetail()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (companySubscriptionDetails === undefined) {
    return <PageLoader />;
  }

  if (productOptions.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-20">
        <p className="text-muted mb-4">
          You don't have any active product subscriptions yet.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => navigate(privateRoutes.Pricing)}
        >
          View Plans
        </button>
      </div>
    );
  }

  if (!selectedOption) {
    return <PageLoader />;
  }

  const handleChange = (selected: any) => {
    setSelectedOption(selected);
  };

  /* ============================================================
     Derived display data — pure rendering, no logic changes
     ============================================================ */
  const planName =
    hireSubscriptionDetails?.subscriptionPricingPlan?.subscriptionPlanProduct
      ?.subscriptionPlan?.subscriptionPlanDisplayName;
  const endDate = hireSubscriptionDetails?.endDate;
  const features =
    hireSubscriptionDetails?.subscriptionPlanProductFeature ?? [];
  const usageItems =
    hireSubscriptionDetails?.companySubscriptionUsage ?? [];
  const daysRemaining = endDate
    ? moment(endDate).startOf("day").diff(moment().startOf("day"), "days")
    : null;
  const daysUrgent = daysRemaining !== null && daysRemaining <= 7;

  /* ============================================================
     Header — shared between Hire and Sapiens views
     ============================================================ */
  const headerComponent = (
    <div className="ni-usage-header">
      <div className="ni-usage-title-wrap">
        <div className="ni-usage-title-icon" aria-hidden="true">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 3v18h18" />
            <path d="M7 15l4-4 4 4 5-5" />
          </svg>
        </div>
        <div className="ni-usage-title-text">
          <h1 className="ni-usage-title">Subscription Usage</h1>
          <div className="ni-usage-subtitle">
            Track your plan, features, and consumption
          </div>
        </div>
      </div>
      {productOptions.length > 1 && (
        <div className="ni-usage-select-wrap">
          <Select
            value={selectedOption}
            onChange={handleChange}
            options={productOptions}
            placeholder="Select a product"
            className="react-select-styled p-0 py-1 react-select-sm w-200px border-select"
            classNamePrefix="react-select"
          />
        </div>
      )}
    </div>
  );

  /* ============================================================
     Sapiens variant — unchanged logic, just rendered inside root
     ============================================================ */
  if (selectedOption?.value === "SAPIENS") {
    return (
      <div className="ni-usage-root">
        {headerComponent}
        <SapiensUsage />
      </div>
    );
  }

  /* ============================================================
     HIRE view — main render
     ============================================================ */
  return (
    <div className="ni-usage-root">
      {headerComponent}

      {/* PLAN HERO CARD */}
      <div className="ni-usage-plan-hero">
        <div className="ni-usage-plan-top">
          <div className="ni-usage-plan-meta">
            <div className="ni-usage-plan-eyebrow">Current plan</div>
            <h2 className="ni-usage-plan-name">{planName}</h2>
            <div className="ni-usage-plan-pills">
              {endDate && (
                <span className="ni-usage-plan-pill ni-usage-plan-pill--active">
                  Active until {moment(endDate).format("Do MMM YYYY")}
                </span>
              )}
              {daysRemaining !== null && daysRemaining >= 0 && (
                <span
                  className={`ni-usage-plan-pill ni-usage-plan-pill--days ${daysUrgent ? "urgent" : ""
                    }`}
                >
                  {daysRemaining === 0
                    ? "Expires today"
                    : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(privateRoutes.Pricing)}
            className="ni-usage-plan-cta"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
            Upgrade Plan
          </button>
        </div>

        <ul className="ni-usage-feature-grid" aria-label="Plan features">
          {features.map((item: any) => (
            <li
              key={item.subscriptionPlanProductFeatureGuid}
              className="ni-usage-feature-row"
            >
              <span className="ni-usage-feature-row-icon" aria-hidden="true">
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="ni-usage-feature-row-text">
                {item.feature?.featuresDisplayName && (
                  <span className="ni-usage-feature-row-label">
                    {item.feature.featuresDisplayName}:{" "}
                  </span>
                )}
                {item.displayName}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* MAIN GRID: graphs (8) + usage breakdown (4) */}
      <div className="row g-3 g-md-4">
        <div className="col-12 col-lg-8">
          {isDataReady && (
            <div className="ni-usage-graphs-stack">
              <UsagesGraphComponent
                creationTimestamps={
                  interviewsconductedGraphData?.creationTimestamps ?? []
                }
                selectId={interviewsconductedGraphData?.features.featuresGuid}
                title={"Interviews conducted"}
              />
              <UsagesGraphComponent
                creationTimestamps={
                  jobsPostedGraphData?.creationTimestamps ?? []
                }
                selectId={jobsPostedGraphData?.features.featuresGuid}
                title={"Jobs posted"}
              />
              <UsagesGraphComponent
                creationTimestamps={
                  resumesUploadedGraphDat?.creationTimestamps ?? []
                }
                selectId={"Resumes_uploaded"}
                title={"Resumes uploaded"}
              />
            </div>
          )}
        </div>

        <div className="col-12 col-lg-4">
          <div className="ni-usage-card">
            <div className="ni-usage-card-header">
              <h3 className="ni-usage-card-title">
                <div className="ni-usage-card-title-icon" aria-hidden="true">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="20" x2="18" y2="10" />
                    <line x1="12" y1="20" x2="12" y2="4" />
                    <line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                </div>
                Usage breakdown
              </h3>
              <div className="ni-usage-card-meta">
                {usageItems.length}{" "}
                {usageItems.length === 1 ? "metric" : "metrics"}
              </div>
            </div>

            {usageItems.map((item: any) => {
              const { percentage, label, iconName } = getFeatureDetails(
                item.currentCount,
                item.currentCount + item.totalAvailableCount,
                item.features.featuresDisplayName,
              );

              let iconElement: React.ReactNode;
              let toneClass = "";
              if (iconName === "AI") {
                iconElement = <RiRobot2Fill />;
                toneClass = "ai";
              } else if (iconName === "round") {
                iconElement = <FaLayerGroup />;
                toneClass = "layer";
              } else if (iconName === "schedule") {
                iconElement = <MdScheduleSend />;
                toneClass = "schedule";
              } else {
                iconElement = <KTIcon iconName={iconName} className="" />;
              }

              return (
                <div
                  key={item.features.featuresGuid}
                  className="ni-usage-item"
                >
                  <div className="ni-usage-item-header">
                    <div className="ni-usage-item-label-wrap">
                      <div
                        className={`ni-usage-item-icon ${toneClass}`}
                        aria-hidden="true"
                      >
                        {iconElement}
                      </div>
                      <div className="ni-usage-item-label">{label}</div>
                    </div>
                    <div className="ni-usage-item-count">
                      <strong>{item.currentCount}</strong> of{" "}
                      {item.currentCount + item.totalAvailableCount} added
                    </div>
                  </div>
                  <UsageProgressBar completion={percentage} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionUsage;
